import React from "react";

import { SelectedIcon } from "../../Icons";
import { concatStringFromClassesArrayList } from "../../Utils";

export interface Props {
  pages: Array<Page>;
  currentPageNumber: number;
  previous?: string;
  next?: string;
  classes?: string;
  hideRangeIndicator?: boolean;
  dataTestId?: string;
}

export interface Page {
  url: string;
  onClick: () => void;
}

export const Pagination = (props: Props) => {
  let currentPage: number;
  if (props.currentPageNumber < 1) {
    currentPage = 1;
  } else if (props.currentPageNumber > props.pages.length) {
    currentPage = props.pages.length;
  } else {
    currentPage = props.currentPageNumber;
  }

  const getPageNumbers = (): string[] => {
    const pageRange = 1;
    const pages: string[] = [];
    let start = Math.max(1, currentPage - pageRange);
    let end = Math.min(props.pages.length, currentPage + pageRange);
    if (currentPage - pageRange > 1) {
      pages.push("1");
      if (start - 1 === 2) {
        pages.push("2");
      } else if (currentPage - pageRange > 2) {
        pages.push("...");
      }
    }

    for (let i = start; i <= end; i++) {
      pages.push(i.toString());
    }

    if (currentPage + pageRange < props.pages.length) {
      if (props.pages.length - end === 2) {
        pages.push((props.pages.length - 1).toString());
      } else if (currentPage + pageRange < props.pages.length - 1) {
        pages.push("...");
      }
      pages.push(props.pages.length.toString());
    }
    return pages;
  };

  const getAriaLabel = (item: string): string => {
    switch (item) {
      case "1": {
        return "Go to the first page (Page 1)";
      }
      case currentPage.toString():
        return `Current page (Page ${currentPage} of ${props.pages.length})`;
      case props.pages.length.toString(): {
        return `Go to the last page (Page ${props.pages.length})`;
      }
      default:
        return `Go to page ${item}`;
    }
  };

  const handleClick = (e: any, index: number) => {
    e.preventDefault();
    props.pages[index].onClick();
  };

  const classList = {
    defaultClass: "ons-pagination",
    optionalClasses: [props.classes ? props.classes : null],
  };
  const className = concatStringFromClassesArrayList(classList);

  const pageNumberList = getPageNumbers();

  return (
    <nav
      className={className}
      aria-label={`Pagination (Page ${currentPage} of ${props.pages?.length})`}
      data-testid={props.dataTestId}
    >
      <div className="ons-pagination__position">
        Page {currentPage} of {props.pages?.length}
      </div>
      <ul className="ons-pagination__items">
        {currentPage !== 1 && (
          <li className="ons-pagination__item ons-pagination__item--previous">
            <a
              href={props.pages[currentPage - 2].url}
              className="ons-pagination__link ons-pagination__link--no-underline"
              rel="prev"
              aria-label={`Go to the previous page (Page ${currentPage - 1})`}
              onClick={(e) => handleClick(e, currentPage - 2)}
            >
              <SelectedIcon iconType="PreviousArrow" />
              <span className="ons-pagination__link-text">
                {props.previous ? props.previous : "Previous"}
              </span>
            </a>
          </li>
        )}
        {pageNumberList.map((item: string, index: number) => {
          if (item === "...") {
            return (
              <li
                className="ons-pagination__item ons-pagination__item--gap"
                key={index + 1}
                data-testid={`pagination-gap-${
                  (index === 1 && "left") ||
                  (index === pageNumberList.length - 2 && "right")
                }`}
              >
                &hellip;
              </li>
            );
          } else {
            return (
              <li
                className={`ons-pagination__item ${
                  currentPage === Number(item)
                    ? "ons-pagination__item--current"
                    : ""
                }`}
                key={index + 1}
                data-testid={`pagination-${Number(item)}`}
              >
                <a
                  href={props.pages[Number(item) - 1].url}
                  className="ons-pagination__link"
                  aria-label={getAriaLabel(item)}
                  onClick={(e) => handleClick(e, Number(item) - 1)}
                  {...(currentPage - 1 === Number(item) && {
                    rel: "prev",
                  })}
                  {...(currentPage + 1 === Number(item) && {
                    rel: "next",
                  })}
                  {...(currentPage === Number(item) && {
                    "aria-current": "true",
                  })}
                >
                  {item}
                </a>
              </li>
            );
          }
        })}
        {currentPage !== props.pages.length && (
          <li className="ons-pagination__item ons-pagination__item--next">
            <a
              href={props.pages[currentPage].url}
              className="ons-pagination__link ons-pagination__link--no-underline"
              rel="next"
              aria-label={`Go to the next page (Page ${currentPage + 1})`}
              onClick={(e) => handleClick(e, currentPage)}
            >
              <span className="ons-pagination__link-text">
                {props.next ? props.next : "Next"}
              </span>
              <SelectedIcon iconType="NextArrow" />
            </a>
          </li>
        )}
      </ul>
    </nav>
  );
};
